/**
 * ChunkedVideoManager - Utility for managing chunked video assets
 *
 * Provides helper functions for:
 * - Converting local time to chunk index
 * - Building chunk URLs from asset metadata
 * - Registering chunks with MediaStreamManager
 * - Calculating time within a chunk for video seeking
 * - Multi-resolution support (360p, 540p, 720p)
 *
 * URL Resolution Pattern:
 * Builds clean CloudFront URLs without query params - relies on signed cookies
 * sent via `credentials: 'include'` / `crossOrigin = 'use-credentials'`.
 * Falls back to legacy baseUrl concatenation for backward compatibility.
 */

import type { Asset, ChunkedVideoMetadata, ProxyResolution } from "../types";
import type { MediaStreamManager } from "./MediaStreamManager";
import { type CloudFrontAuth } from "@/utils/derivedAssetUrls";

/**
 * Build a clean CloudFront URL for cookie-based authentication.
 * Does NOT include query params - relies on signed cookies sent via credentials.
 *
 * @param domain - CloudFront domain (e.g., "assets.stud1o.org")
 * @param path - Path starting with /projects/... (no leading slash needed)
 */
export function buildCloudFrontUrl(domain: string, path: string): string {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `https://${domain}${cleanPath}`;
}

/**
 * Build a clean CloudFront URL for a derived asset using cookie-based auth.
 * Unlike resolveDerivedAssetUrl, this does NOT append query params.
 */
export function buildCloudFrontAssetUrl(
  projectId: string,
  assetId: string,
  pathTemplate: string,
  params: Record<string, string>,
  index: number | undefined,
  domain: string,
): string {
  let path = pathTemplate;

  for (const [key, value] of Object.entries(params)) {
    path = path.replace(`{${key}}`, value);
  }

  if (index !== undefined) {
    path = path.replace("{index}", String(index).padStart(6, "0"));
  }

  return buildCloudFrontUrl(
    domain,
    `/projects/${projectId}/${assetId}/${path}`,
  );
}

/**
 * Auth getter function type - returns current CloudFront auth
 * Used for lazy URL resolution to prevent signed URL expiration
 */
export type AuthGetter = () => CloudFrontAuth | undefined;

/**
 * Extract the file path from a signed CloudFront URL
 * E.g., https://xxx.cloudfront.net/projects/proj-123/asset-456/original.jpg?Policy=...
 *    -> original.jpg
 */
export function extractPathFromSignedUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    // Path format: /projects/{projectId}/{assetId}/{filePath}
    const pathParts = urlObj.pathname.split("/");
    // Find "projects" index and get everything after assetId
    const projectsIdx = pathParts.indexOf("projects");
    if (projectsIdx >= 0 && pathParts.length > projectsIdx + 3) {
      // Everything after /projects/{projectId}/{assetId}/
      return pathParts.slice(projectsIdx + 3).join("/");
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Extract projectId and assetId from a signed CloudFront URL
 * E.g., https://xxx.cloudfront.net/projects/proj-123/asset-456/original.jpg?Policy=...
 *    -> { projectId: "proj-123", assetId: "asset-456" }
 */
export function extractIdsFromSignedUrl(
  url: string,
): { projectId: string; assetId: string } | null {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split("/");
    const projectsIdx = pathParts.indexOf("projects");
    if (projectsIdx >= 0 && pathParts.length > projectsIdx + 2) {
      return {
        projectId: pathParts[projectsIdx + 1],
        assetId: pathParts[projectsIdx + 2],
      };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Create a URL resolver for a non-chunked asset
 * Extracts path info from the URL and rebuilds with fresh auth when called
 *
 * Handles two URL formats:
 * - Standard: /projects/{projectId}/{assetId}/{path} -> uses resolveDerivedAssetUrl
 * - Direct:   /projects/{projectId}/{filename}       -> uses refreshSignedUrl
 *
 * This ensures workflow output images (which are stored directly in the project
 * directory without an asset ID subdirectory) get fresh auth when loaded.
 */
export function createNonChunkedUrlResolver(
  originalUrl: string,
  authGetter: AuthGetter,
): (() => string) | undefined {
  // First, try to parse as standard asset URL (/projects/{projectId}/{assetId}/{path})
  const path = extractPathFromSignedUrl(originalUrl);
  const ids = extractIdsFromSignedUrl(originalUrl);

  if (path && ids) {
    // Standard asset URL - build clean CloudFront URL (cookies provide auth)
    return () => {
      const currentAuth = authGetter();
      if (currentAuth) {
        return buildCloudFrontAssetUrl(
          ids.projectId,
          ids.assetId,
          path,
          {},
          undefined,
          currentAuth.domain,
        );
      }
      // Even without auth, strip query params - cookies provide auth, not signed URL params
      // Use original URL's hostname as domain (it's already the CDN domain)
      try {
        const urlObj = new URL(originalUrl);
        return buildCloudFrontUrl(
          urlObj.hostname,
          `/projects/${ids.projectId}/${ids.assetId}/${path}`,
        );
      } catch {
        return originalUrl;
      }
    };
  }

  // Fallback: Direct project file URL (/projects/{projectId}/{filename})
  // This handles workflow output images and other files stored directly in project directory
  // Use refreshSignedUrl which strips query params and preserves the full path after projectId
  return () => {
    const currentAuth = authGetter();
    return refreshSignedUrl(originalUrl, currentAuth);
  };
}

/**
 * Rebuild ANY CloudFront signed URL with fresh auth
 * Works for both standard asset URLs and workflow-generated files
 *
 * URL formats supported:
 * - Standard: /projects/{projectId}/{assetId}/{path} -> uses resolveDerivedAssetUrl
 * - Direct:   /projects/{projectId}/{filename}       -> rebuilds with path directly
 *
 * @returns Fresh signed URL, or original URL if parsing fails or no auth provided
 */
export function refreshSignedUrl(
  originalUrl: string,
  auth: CloudFrontAuth | null | undefined,
): string {
  try {
    const urlObj = new URL(originalUrl);

    // ALWAYS strip query params from CDN URLs - cookies provide auth, not signed URL params
    const isCdnUrl =
      urlObj.hostname.includes("cloudfront.net") ||
      urlObj.hostname.includes("s3.") ||
      urlObj.hostname.includes("stud1o.org") ||
      (auth?.domain && urlObj.hostname === auth.domain);

    if (!isCdnUrl) {
      return originalUrl;
    }

    const pathParts = urlObj.pathname.split("/").filter(Boolean); // Remove empty strings

    // Find "projects" in path
    const projectsIdx = pathParts.indexOf("projects");
    if (projectsIdx < 0 || pathParts.length < projectsIdx + 2) {
      // Can't parse structure, but still strip query params for CDN URLs
      const domain = auth?.domain || urlObj.hostname;
      return buildCloudFrontUrl(domain, urlObj.pathname);
    }

    const projectId = pathParts[projectsIdx + 1];
    const remainingPath = pathParts.slice(projectsIdx + 2).join("/");

    if (!remainingPath) {
      // No remaining path, but still strip query params
      const domain = auth?.domain || urlObj.hostname;
      return buildCloudFrontUrl(domain, urlObj.pathname);
    }

    // Build clean CloudFront URL (cookies provide auth, no query params needed)
    const domain = auth?.domain || urlObj.hostname;
    return buildCloudFrontUrl(
      domain,
      `/projects/${projectId}/${remainingPath}`,
    );
  } catch {
    // URL parsing failed - return original (might not be a URL)
    return originalUrl;
  }
}

/**
 * Quality override setting
 * - "auto": Select resolution based on device tier
 * - ProxyResolution: Force a specific resolution
 */
export type QualityOverride = "auto" | ProxyResolution;

/**
 * Current quality override setting (module-level state)
 * Default: "auto" (device tier determines resolution)
 */
let currentQualityOverride: QualityOverride = "auto";

/**
 * Set the quality override
 * @param override - "auto" or specific resolution ("360p", "540p", "720p")
 */
export function setQualityOverride(override: QualityOverride): void {
  currentQualityOverride = override;
  // Notify listeners (for UI updates)
  qualityChangeListeners.forEach((listener) => listener(override));
}

/**
 * Get the current quality override
 */
export function getQualityOverride(): QualityOverride {
  return currentQualityOverride;
}

/**
 * Quality change listeners
 */
const qualityChangeListeners: Array<(quality: QualityOverride) => void> = [];

/**
 * Subscribe to quality changes
 * @returns Unsubscribe function
 */
export function onQualityChange(
  listener: (quality: QualityOverride) => void,
): () => void {
  qualityChangeListeners.push(listener);
  return () => {
    const index = qualityChangeListeners.indexOf(listener);
    if (index >= 0) qualityChangeListeners.splice(index, 1);
  };
}

/**
 * Get the effective resolution based on override and device tier
 * @param availableResolutions - Resolutions available for the asset
 * @param deviceTier - Device capability tier (from AdaptiveBufferConfig)
 */
export function getEffectiveResolution(
  availableResolutions: ProxyResolution[] | undefined,
  deviceTier: "low" | "medium" | "high" | "ultra",
): ProxyResolution | null {
  // If no multi-resolution support, return null (use legacy path)
  if (!availableResolutions || availableResolutions.length === 0) {
    return null;
  }

  // If override is set to a specific resolution, use it if available
  if (currentQualityOverride !== "auto") {
    if (availableResolutions.includes(currentQualityOverride)) {
      return currentQualityOverride;
    }
    // Fallback to closest available if override not available
  }

  // Auto mode: select based on device tier
  const tierToResolution: Record<string, ProxyResolution> = {
    low: "360p",
    medium: "540p",
    high: "720p",
    ultra: "720p",
  };

  const preferredResolution = tierToResolution[deviceTier];

  // Return preferred if available, otherwise closest available
  if (availableResolutions.includes(preferredResolution)) {
    return preferredResolution;
  }

  // Fallback: return highest available that's <= preferred
  const resolutionOrder: ProxyResolution[] = ["360p", "540p", "720p"];
  const preferredIndex = resolutionOrder.indexOf(preferredResolution);

  for (let i = preferredIndex; i >= 0; i--) {
    if (availableResolutions.includes(resolutionOrder[i])) {
      return resolutionOrder[i];
    }
  }

  // Last resort: return first available
  return availableResolutions[0];
}

/**
 * Get the chunk index for a given local time
 */
export function getChunkIndex(
  localTime: number,
  chunkDuration: number,
): number {
  return Math.floor(localTime / chunkDuration);
}

/**
 * Get the time within a chunk (for video element seeking)
 */
export function getTimeInChunk(
  localTime: number,
  chunkDuration: number,
): number {
  return localTime % chunkDuration;
}

/**
 * Build chunk filename from pattern and index.
 * Pattern uses printf-style %Nd for zero-padded index (e.g., %06d).
 */
export function getChunkFilename(pattern: string, chunkIndex: number): string {
  return pattern.replace(/%(\d*)d/, (_match, width) => {
    return chunkIndex.toString().padStart(parseInt(width) || 0, "0");
  });
}

/**
 * Build full chunk URL using resolveDerivedAssetUrl pattern
 * Only call this for true chunked videos (chunkCount > 1) where chunkPattern exists
 *
 * @param chunked - Chunked video metadata
 * @param chunkIndex - Chunk index
 * @param resolution - Optional resolution override (if asset has multi-resolution support)
 * @param auth - Optional CloudFront auth for signed URLs
 */
export function getChunkUrl(
  chunked: ChunkedVideoMetadata,
  chunkIndex: number,
  resolution?: ProxyResolution | null,
  auth?: CloudFrontAuth,
): string {
  // New pattern: build clean CloudFront URL when pathTemplate AND auth are available
  // Cookies provide auth, so no query params needed
  if (chunked.pathTemplate && chunked.projectId && chunked.assetId && auth) {
    const effectiveResolution =
      resolution && chunked.availableResolutions?.includes(resolution)
        ? resolution
        : chunked.params?.resolution;

    return buildCloudFrontAssetUrl(
      chunked.projectId,
      chunked.assetId,
      chunked.pathTemplate,
      { ...chunked.params, resolution: effectiveResolution || "" },
      chunkIndex,
      auth.domain,
    );
  }

  // Fallback: use pre-resolved baseUrl (which has CDN domain baked in from asset adapter)
  if (!chunked.chunkPattern) {
    throw new Error(
      "getChunkUrl called without chunkPattern - use versions[].src for full videos",
    );
  }
  if (!chunked.baseUrl) {
    throw new Error(
      "getChunkUrl called without baseUrl or pathTemplate - cannot construct URL",
    );
  }

  const filename = getChunkFilename(chunked.chunkPattern, chunkIndex);

  // If resolution specified and asset has multi-resolution support, use subdirectory
  if (resolution && chunked.availableResolutions?.includes(resolution)) {
    return `${chunked.baseUrl}/${resolution}/${filename}`;
  }

  // Legacy format: chunks at root level
  return `${chunked.baseUrl}/${filename}`;
}

/**
 * Generate asset ID for a specific chunk
 */
export function getChunkAssetId(
  baseAssetId: string,
  chunkIndex: number,
): string {
  return `${baseAssetId}_chunk_${chunkIndex}`;
}

/**
 * Check if an asset is chunked
 */
export function isChunkedAsset(asset: Asset): boolean {
  return asset.type === "video" && asset.chunked !== undefined;
}

/**
 * Get the asset ID for a given local time
 * Returns chunk-specific ID for chunked assets, or original ID for non-chunked
 */
export function getAssetIdForTime(asset: Asset, localTime: number): string {
  if (!asset.chunked) {
    return asset.id;
  }

  const chunkIndex = getChunkIndex(localTime, asset.chunked.chunkDuration);
  // Clamp to valid chunk range
  const clampedIndex = Math.min(chunkIndex, asset.chunked.chunkCount - 1);
  return getChunkAssetId(asset.id, clampedIndex);
}

/**
 * Get all chunk asset IDs for an asset
 */
export function getAllChunkAssetIds(asset: Asset): string[] {
  if (!asset.chunked) {
    return [asset.id];
  }

  const ids: string[] = [];
  for (let i = 0; i < asset.chunked.chunkCount; i++) {
    ids.push(getChunkAssetId(asset.id, i));
  }
  return ids;
}

/**
 * Register all chunks for a chunked asset with MediaStreamManager
 *
 * @param asset - The asset to register
 * @param mediaManager - MediaStreamManager instance
 * @param deviceTier - Optional device tier for resolution selection (defaults to "medium")
 * @param auth - Optional static CloudFront auth (deprecated, use authGetter)
 * @param authGetter - Function to get current CloudFront auth at load time (recommended)
 */
export function registerChunkedAsset(
  asset: Asset,
  mediaManager: MediaStreamManager,
  deviceTier: "low" | "medium" | "high" | "ultra" = "medium",
  auth?: CloudFrontAuth,
  authGetter?: AuthGetter,
): void {
  if (!asset.chunked) {
    // Non-chunked: register as single asset using version src
    const selectedVersion = asset.versions?.find(
      (v) => v.id === asset.selectedVersionId,
    );
    const url = selectedVersion?.src || asset.thumbnail || "";

    // Create URL resolver for lazy resolution (prevents signed URL expiration)
    // Extracts path from URL and rebuilds with fresh auth when called
    const urlResolver = authGetter
      ? createNonChunkedUrlResolver(url, authGetter)
      : undefined;

    mediaManager.registerAsset(asset.id, url, "video", urlResolver);
    return;
  }

  // Full video mode: chunkCount === 1, use version src directly
  // This allows filmstrip metadata to remain while playing full video files
  // IMPORTANT: Still register as chunk_0 to match TimelineCompiler's asset ID format
  if (asset.chunked.chunkCount === 1) {
    const selectedVersion = asset.versions?.find(
      (v) => v.id === asset.selectedVersionId,
    );
    const url = selectedVersion?.src || asset.thumbnail || "";
    const chunkId = getChunkAssetId(asset.id, 0);

    // Create URL resolver for lazy resolution (prevents signed URL expiration)
    let urlResolver: (() => string) | undefined;

    if (authGetter) {
      // Check if chunked metadata has pathTemplate (preferred)
      if (asset.chunked?.pathTemplate) {
        const chunked = asset.chunked; // Capture for closure
        urlResolver = () => {
          const currentAuth = authGetter();
          if (currentAuth) {
            return getChunkUrl(chunked, 0, null, currentAuth);
          }
          return url;
        };
      } else {
        // Fallback: extract path from URL and rebuild with fresh auth
        urlResolver = createNonChunkedUrlResolver(url, authGetter);
      }
    }

    mediaManager.registerAsset(chunkId, url, "video", urlResolver);
    return;
  }

  // Determine resolution to use (based on device tier or override)
  const resolution = getEffectiveResolution(
    asset.chunked.availableResolutions,
    deviceTier,
  );

  // Register each chunk as separate asset (true chunked videos)
  // Use URL resolvers for lazy resolution to prevent signed URL expiration
  const chunked = asset.chunked; // Capture for closure
  for (let i = 0; i < chunked.chunkCount; i++) {
    const chunkId = getChunkAssetId(asset.id, i);
    const chunkIndex = i; // Capture for closure
    const chunkUrl = getChunkUrl(chunked, i, resolution, auth);

    // Create URL resolver that gets fresh auth at load time
    const urlResolver = authGetter
      ? () => {
          const currentAuth = authGetter();
          if (currentAuth) {
            return getChunkUrl(chunked, chunkIndex, resolution, currentAuth);
          }
          return chunkUrl; // Fallback to static URL
        }
      : undefined;

    mediaManager.registerAsset(chunkId, chunkUrl, "video", urlResolver);
  }
}

/**
 * Get chunk URL with current quality settings
 * Use this when you need to get a chunk URL outside of registerChunkedAsset
 *
 * @param chunked - Chunked video metadata
 * @param chunkIndex - Chunk index
 * @param deviceTier - Device capability tier for resolution selection
 * @param auth - Optional CloudFront auth for signed URLs
 */
export function getChunkUrlWithQuality(
  chunked: ChunkedVideoMetadata,
  chunkIndex: number,
  deviceTier: "low" | "medium" | "high" | "ultra" = "medium",
  auth?: CloudFrontAuth,
): string {
  const resolution = getEffectiveResolution(
    chunked.availableResolutions,
    deviceTier,
  );
  return getChunkUrl(chunked, chunkIndex, resolution, auth);
}

/**
 * Get chunk indices that are visible within a time range
 * Useful for preloading chunks around the playhead
 */
export function getChunksInRange(
  chunked: ChunkedVideoMetadata,
  startTime: number,
  endTime: number,
): number[] {
  const startChunk = Math.max(
    0,
    getChunkIndex(startTime, chunked.chunkDuration),
  );
  const endChunk = Math.min(
    chunked.chunkCount - 1,
    getChunkIndex(endTime, chunked.chunkDuration),
  );

  const chunks: number[] = [];
  for (let i = startChunk; i <= endChunk; i++) {
    chunks.push(i);
  }
  return chunks;
}

/**
 * Get chunks to preload based on current time and lookahead buffer
 */
export function getChunksToPreload(
  chunked: ChunkedVideoMetadata,
  currentTime: number,
  lookAheadSeconds: number,
): number[] {
  return getChunksInRange(chunked, currentTime, currentTime + lookAheadSeconds);
}

/**
 * Filmstrip utility: get sprite frame index for a given time
 */
export function getFilmstripFrameIndex(
  time: number,
  duration: number,
  frameCount: number,
): number {
  const progress = Math.max(0, Math.min(1, time / duration));
  return Math.min(Math.floor(progress * frameCount), frameCount - 1);
}

/**
 * Filmstrip utility: get sprite X position in spritesheet
 * Assumes horizontal single-row layout
 */
export function getFilmstripSpriteX(
  frameIndex: number,
  frameWidth: number,
): number {
  return frameIndex * frameWidth;
}

/**
 * Calculate frame width from filmstrip metadata and aspect ratio
 */
export function calculateFilmstripFrameWidth(
  frameHeight: number,
  aspectRatio: number,
): number {
  return Math.round(frameHeight * aspectRatio);
}
